import { useCommunityContext } from 'components/CommunityContext';
import { useState } from 'react';
import * as filestack from 'filestack-js';
import { AWS_S3_URL } from 'utils/constants';
import { getFileType } from 'utils/fileHelper';

interface UploadFile {
  file: File;
  tempId: string;
}

export default function useFileOperations() {
  const { community, filestackSecurity } = useCommunityContext();
  const [isUploading, setIsUploading] = useState(false);

  const filestackClient = filestack.init(process.env.FILESTACK_API_KEY);

  const generatePreviews = (files: File[]) => {
    const previews = files.map((file) => ({
      fileName: file.name,
      fileSizeInBytes: file.size,
      isLoading: true,
      url: URL.createObjectURL(file),
      attachmentType: getFileType(file.type),
      // Add a temporary ID to help with replacement later
      tempId: Math.random().toString(36).substring(2),
    }));
    return previews;
  };

  const uploadFiles = async (filesToUpload: UploadFile[]) => {
    setIsUploading(true);

    try {
      const storageOptions = {
        access: 'public',
        container: process.env.S3_IMPORT_BUCKET,
        location: 's3',
        path: community.s3UploadPath,
        region: process.env.AWS_REGION,
      };

      const files = filesToUpload.map((file) => file.file);

      const uploadedFiles = await filestackClient.multiupload(
        files,
        {},
        storageOptions,
        null,
        filestackSecurity
      );

      const newAttachments = uploadedFiles.map((uploadedFile, index) => ({
        fileName: uploadedFile.filename,
        fileSizeInBytes: uploadedFile.size,
        filestackId: uploadedFile.handle,
        s3Key: uploadedFile.key,
        s3Bucket: uploadedFile.container,
        url: `https://${uploadedFile.container}.${AWS_S3_URL}/${uploadedFile.key}`,
        attachmentUrl: '', // only for video attachments
        attachmentType: getFileType(uploadedFile.mimetype),
        tempId: filesToUpload[index].tempId,
      }));

      setIsUploading(false);
      return { attachments: newAttachments, error: null };
    } catch (error) {
      setIsUploading(false);
      return { attachments: [], error };
    }
  };

  return {
    isUploading,
    generatePreviews,
    uploadFiles,
  };
}
